/**
 * Customer display helpers
 *
 * Pure functions for safely deriving display values from a Customer record.
 *
 * The backend Customer schema is sparse: most fields are optional, `status`
 * may be an empty string for legacy / admin-created records, and
 * `last_seen_at` is often unset for customers who only have first-touch
 * data via `consumer_sightings`.
 *
 * These helpers centralise the "what to show in the UI when a field is
 * missing" rules so every component renders consistently and never
 * crashes on a missing property.
 */

import type { Customer, CustomerStatus } from './types'
import type { StatusKind } from '@/shared/components/StatusDot'

// ── Display name ────────────────────────────────────────────────────────────

/**
 * The best display name we can produce for a customer.
 *
 * Order of preference: `name` → email local part → first 16 chars of `id`.
 * Always returns a non-empty string.
 */
export function customerDisplayName(c: Customer): string {
  const trimmed = c.name?.trim()
  if (trimmed) return trimmed
  if (c.email) {
    const local = c.email.split('@')[0]
    if (local) return local
  }
  return c.id.slice(0, 16)
}

// ── Status display ──────────────────────────────────────────────────────────

const STATUS_LABELS: Record<Exclude<CustomerStatus, ''>, string> = {
  lead: 'Lead',
  registered: 'Registered',
  active: 'Active',
  churned: 'Churned',
}

const STATUS_KINDS: Record<Exclude<CustomerStatus, ''>, StatusKind> = {
  lead: 'info',
  registered: 'info',
  active: 'success',
  churned: 'neutral',
}

/**
 * Resolve the effective lifecycle status for display.
 *
 * Backend may return an empty string for legacy customers. We surface that
 * as `'unknown'` so the UI can label it explicitly rather than silently
 * showing nothing. Any value the backend sends that isn't in the canonical
 * enum is also treated as `'unknown'` (defensive).
 */
export function effectiveStatus(
  c: Customer
): Exclude<CustomerStatus, ''> | 'unknown' {
  const s = c.status
  if (s === 'lead' || s === 'registered' || s === 'active' || s === 'churned') {
    return s
  }
  return 'unknown'
}

export function statusLabel(c: Customer): string {
  const s = effectiveStatus(c)
  return s === 'unknown' ? 'Unknown' : STATUS_LABELS[s]
}

export function statusKind(c: Customer): StatusKind {
  const s = effectiveStatus(c)
  return s === 'unknown' ? 'neutral' : STATUS_KINDS[s]
}

// ── Activity / "last seen" ──────────────────────────────────────────────────

/**
 * Compute the effective "last seen" unix timestamp for a customer.
 *
 * Order of preference:
 *   1. `last_seen_at` — explicit backend signal.
 *   2. The maximum value across `consumer_sightings` (per-consumer
 *      first-seen timestamps; for many customers this is the only
 *      activity signal we have).
 *   3. `email_verified_at`.
 *   4. `updated`.
 *   5. `created` — the floor: every customer has this. Returning `created`
 *      means "we know they exist as of this time" which is strictly more
 *      truthful than rendering "Never".
 *
 * Returns `undefined` only if the customer has none of these (which
 * should not happen for a valid record because `created` is required).
 */
export function effectiveLastSeenAt(c: Customer): number | undefined {
  if (typeof c.last_seen_at === 'number' && c.last_seen_at > 0) {
    return c.last_seen_at
  }

  const sightings = c.consumer_sightings
  if (sightings) {
    let max = 0
    for (const ts of Object.values(sightings)) {
      if (typeof ts === 'number' && ts > max) max = ts
    }
    if (max > 0) return max
  }

  if (typeof c.email_verified_at === 'number' && c.email_verified_at > 0) {
    return c.email_verified_at
  }
  if (typeof c.updated === 'number' && c.updated > 0) {
    return c.updated
  }
  if (typeof c.created === 'number' && c.created > 0) {
    return c.created
  }
  return undefined
}

/**
 * The activity *source* — used for tooltips so operators can tell whether
 * "last active" came from a real signal or a fallback.
 */
export function lastSeenSource(
  c: Customer
):
  | 'last_seen'
  | 'consumer_sighting'
  | 'email_verified'
  | 'updated'
  | 'created'
  | 'unknown' {
  if (typeof c.last_seen_at === 'number' && c.last_seen_at > 0) {
    return 'last_seen'
  }
  if (c.consumer_sightings) {
    for (const ts of Object.values(c.consumer_sightings)) {
      if (typeof ts === 'number' && ts > 0) return 'consumer_sighting'
    }
  }
  if (typeof c.email_verified_at === 'number' && c.email_verified_at > 0) {
    return 'email_verified'
  }
  if (typeof c.updated === 'number' && c.updated > 0) {
    return 'updated'
  }
  if (typeof c.created === 'number' && c.created > 0) {
    return 'created'
  }
  return 'unknown'
}

const LAST_SEEN_SOURCE_LABELS: Record<
  ReturnType<typeof lastSeenSource>,
  string
> = {
  last_seen: 'Last activity',
  consumer_sighting: 'First seen by app',
  email_verified: 'Email verified',
  updated: 'Last updated',
  created: 'Account created',
  unknown: 'Unknown',
}

export function lastSeenSourceLabel(c: Customer): string {
  return LAST_SEEN_SOURCE_LABELS[lastSeenSource(c)]
}

// ── Email domain ────────────────────────────────────────────────────────────

/**
 * Extract the domain part of an email address. Returns `''` if the email
 * is not a valid `local@domain` format.
 */
export function extractDomain(email: string | undefined | null): string {
  if (!email) return ''
  const idx = email.lastIndexOf('@')
  if (idx <= 0 || idx === email.length - 1) return ''
  return email.slice(idx + 1).toLowerCase()
}

// ── Consumer / origin display ───────────────────────────────────────────────

/**
 * The consumer identifier we should show as the customer's "primary
 * source". Uses `origin.consumer_id` when present, otherwise the first key
 * from `consumer_sightings`.
 */
export function primaryConsumerId(c: Customer): string | undefined {
  if (c.origin?.consumer_id) return c.origin.consumer_id
  const sightings = c.consumer_sightings
  if (sightings) {
    const first = Object.keys(sightings)[0]
    if (first) return first
  }
  return undefined
}
